/**
 * The build identity every signal is stamped with.
 *
 * OneUptime groups telemetry by `service.name` and resolves browser exceptions
 * with the source maps stored under `service.name` + `service.version`. Both
 * sides therefore read this module: the providers take `releaseVersion()` as
 * `service.version`, and the source-map uploader resolves the same value from
 * the same inputs at build time, so a release can never be stamped with one
 * commit and mapped with another.
 *
 * Values are resolved in this order, because the first one is the only one
 * that is identical in the browser bundle and the server bundle:
 *
 *  1. `NEXT_PUBLIC_APP_RELEASE` — injected by next.config.ts from the commit
 *     being built, so client and server cannot disagree.
 *  2. `VERCEL_GIT_COMMIT_SHA` / `GITHUB_SHA` — present on the build worker and
 *     at runtime on Vercel, absent in a browser.
 *  3. `unknown`.
 *
 * @module lib/observability/identity
 */

export const UNKNOWN_RELEASE = 'unknown'
export const UNKNOWN_ENVIRONMENT = 'unknown'

export const RELEASE_ENV_KEY = 'NEXT_PUBLIC_APP_RELEASE'
export const DEPLOYMENT_ENV_KEY = 'NEXT_PUBLIC_DEPLOY_ENV'

/**
 * In the browser bundle Next.js inlines `process.env.NEXT_PUBLIC_*`, but a bare
 * `process` reference is not guaranteed. Each read is therefore guarded, and
 * the static member expression is what makes the inlining work.
 */
function readBuildRelease(): string | undefined {
  try {
    if (typeof process === 'undefined' || !process.env) return undefined
    return process.env.NEXT_PUBLIC_APP_RELEASE
  } catch {
    return undefined
  }
}

function readServerRelease(): string | undefined {
  try {
    if (typeof process === 'undefined' || !process.env) return undefined
    return process.env.VERCEL_GIT_COMMIT_SHA ?? process.env.GITHUB_SHA
  } catch {
    return undefined
  }
}

function readBuildEnvironment(): string | undefined {
  try {
    if (typeof process === 'undefined' || !process.env) return undefined
    return process.env.NEXT_PUBLIC_DEPLOY_ENV
  } catch {
    return undefined
  }
}

function readServerEnvironment(): string | undefined {
  try {
    if (typeof process === 'undefined' || !process.env) return undefined
    return process.env.VERCEL_ENV ?? process.env.NODE_ENV
  } catch {
    return undefined
  }
}

function firstNonEmpty(...candidates: Array<string | undefined>): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim()
      if (trimmed.length > 0) return trimmed
    }
  }
  return undefined
}

/**
 * The release identifier for this build: the commit SHA in production, the
 * deploy's own value in preview, `unknown` when nothing was stamped.
 */
export function releaseVersion(): string {
  return firstNonEmpty(readBuildRelease(), readServerRelease()) ?? UNKNOWN_RELEASE
}

/** `production`, `preview`, `development`, `test` — the deploy's environment. */
export function deploymentEnvironment(): string {
  return firstNonEmpty(readBuildEnvironment(), readServerEnvironment()) ?? UNKNOWN_ENVIRONMENT
}
